/**
 * Demo Document Processing Hook
 * Uses demo API endpoints that don't require authentication
 */

import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { demoOcrGenerate } from '../utils/demoApi';
import { setOcrInputID, setRateLimit as setRateLimitAction } from '../store/reducers';

export const useDemoDocumentProcessing = () => {
  const dispatch = useDispatch();
  const [isGeneratingTaric, setIsGeneratingTaric] = useState(false);
  const [hasGenerationError, setHasGenerationError] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState('');
  const [processingCancelled, setProcessingCancelled] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);

  /**
   * Process documents using demo API
   * @param {File[]} documents - Array of PDF files to process
   * @param {Function} onStatusUpdate - Callback function for status updates
   * @param {string} email - User email address (required for demo)
   * @param {Array} specifyAmount - JSON array of specific amounts/partidas to extract (optional)
   * @param {string} userContext - Additional user context for processing (optional)
   * @param {string} company - Company name (optional; sent to demo API as `company`)
   * @returns {Promise<void>}
   */
  const processDocuments = useCallback(async (
    documents, 
    onStatusUpdate, 
    email,
    specifyAmount = [], 
    userContext = '',
    company = ''
  ) => {
    if (!documents || documents.length === 0) {
      throw new Error('No PDF files uploaded. Please upload files before processing.');
    }

    if (!email || typeof email !== 'string' || email.trim() === '') {
      throw new Error('Email address is required for demo processing.');
    }

    setProcessingCancelled(false);
    setHasGenerationError(false);
    setGenerationErrorMessage('');

    try {
      // Filter to only actual File objects
      const validFiles = documents.filter(file => file instanceof File);
      
      if (validFiles.length === 0) {
        throw new Error('No valid PDF files found');
      }

      // Call demo OCR generate API
      const response = await demoOcrGenerate(
        validFiles,
        email.trim(),
        specifyAmount,
        userContext,
        company
      );

      // Parse response
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        throw new Error('Invalid response from server');
      }

      // Check if response includes rate limit info (even in successful responses)
      // Only update rate limit data, don't show modal based on remaining count
      // Modal should only show when API explicitly returns a rate limit error
      if (responseData.rateLimit) {
        const rateLimitData = {
          remaining: responseData.rateLimit.remaining ?? 0,
          maxRequests: responseData.rateLimit.maxRequests ?? 5
        };
        setRateLimit(rateLimitData); // Update local state
        dispatch(setRateLimitAction(rateLimitData)); // Store in Redux for Demo.jsx
        // Don't show modal for successful responses - only show on explicit error responses
        setRateLimitExceeded(false);
      }

      // Extract job ID from response
      const jobId = responseData.job_id || responseData.jobId;
      
      if (!jobId) {
        console.error('Demo API response missing jobId:', responseData);
        throw new Error('No job ID returned from server');
      }

      console.log('Demo API response received:', { jobId, responseData });

      // Store job ID
      sessionStorage.setItem('activeJobId', jobId);
      dispatch(setOcrInputID(jobId));

      // Update status - pass jobId and proper structure that ProcessingModal expects
      // JobStreamDisplay will handle the streaming automatically
      if (onStatusUpdate) {
        onStatusUpdate({
          jobId: jobId,
          processingStatus: 'processing',
          currentStep: 1,
          completedSteps: [0],
          ...responseData // Include full response data so ProcessingModal can access it
        });
      }

      // Return responseData so ProcessingModal can check for jobId
      return responseData;

    } catch (error) {
      console.error('Error processing documents in demo mode:', error);
      
      // Check if this is a rate limit error
      if (error.isRateLimitError && error.rateLimit) {
          setRateLimitExceeded(true);
        setRateLimit(error.rateLimit); // Update local state
        dispatch(setRateLimitAction(error.rateLimit)); // Store in Redux for Demo.jsx
        setHasGenerationError(false);
        setGenerationErrorMessage('');
        
        if (onStatusUpdate) {
          onStatusUpdate({ status: 'rate_limit_exceeded', rateLimit: error.rateLimit });
        }
        
        // Don't throw error for rate limit - let the modal handle it
        return { rateLimitExceeded: true, rateLimit: error.rateLimit };
      }
      
      setHasGenerationError(true);
      setRateLimitExceeded(false);
      setRateLimit(null);
      
      // Provide more user-friendly error messages
      let userFriendlyMessage = error.message || 'Failed to process documents';
      if (error.message && error.message.includes('Failed to connect')) {
        userFriendlyMessage = 'Unable to connect to the demo API. Please check your network connection or contact support.';
      } else if (error.message && error.message.includes('not configured')) {
        userFriendlyMessage = 'Demo API is not properly configured. Please contact support.';
      }
      
      setGenerationErrorMessage(userFriendlyMessage);
      
      if (onStatusUpdate) {
        onStatusUpdate({ status: 'error', message: userFriendlyMessage });
      }
      
      throw error;
    }
  }, [dispatch, processingCancelled]);

  /**
   * Generate TARIC declaration (placeholder - may need demo-specific implementation)
   * @param {Object} preparedJson - Prepared JSON data
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const generateTaricDeclaration = useCallback(async (preparedJson) => {
    setIsGeneratingTaric(true);
    setHasGenerationError(false);
    setGenerationErrorMessage('');

    try {
      // TODO: Implement demo-specific TARIC generation if needed
      // For now, this is a placeholder
      return { success: true };
    } catch (error) {
      setHasGenerationError(true);
      setGenerationErrorMessage(error.message || 'Failed to generate TARIC declaration');
      return { success: false, error: error.message };
    } finally {
      setIsGeneratingTaric(false);
    }
  }, []);

  return {
    isGeneratingTaric,
    hasGenerationError,
    generationErrorMessage,
    processingCancelled,
    setProcessingCancelled,
    processDocuments,
    generateTaricDeclaration,
    rateLimitExceeded,
    setRateLimitExceeded,
    rateLimit
  };
};

